import React, { useState, useEffect } from 'react';
import { Typography, Container, Box, RadioGroup, FormControlLabel, Radio, Grid, useMediaQuery, Grow, Slide, Card, CardContent, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { questions } from '../data';
import ImageComponent from './ImageComponent';
import CustomStepper from './CustomStepper';

const shuffle = (array) => array.sort(() => Math.random() - 0.5);

const QuestionComponent = ({ navigate, questionData }) => {
    const [selectedOption, setSelectedOption] = useState('');
    const [shuffledOptions, setShuffledOptions] = useState([]);
    const [outcomeTally, setOutcomeTally] = useState({});
    const [hoveredOption, setHoveredOption] = useState(null);
    const [isExiting, setIsExiting] = useState(false);
    const [tempSelectedOutcomeId, setTempSelectedOutcomeId] = useState(null);
    const [completedSteps, setCompletedSteps] = useState({});
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [showStepper, setShowStepper] = useState(false);

    useEffect(() => {
        setSelectedOption('');
        setHoveredOption(null);  // Reset the hover state
        setShuffledOptions(shuffle([...questionData.options]));
    }, [questionData]);

    useEffect(() => {
        // When the first question loads
        if (questionData.id === 1) {
            setShowStepper(true);
        }
    }, [questionData.id]);

    const handleOptionChange = (event) => {
        setSelectedOption(event.target.value);
        const selectedOutcomeId = questionData.options.find(opt => opt.id === event.target.value).outcomeId;

        const selectedOptionData = questionData.options.find(opt => opt.id === event.target.value);
        setCompletedSteps(prevSteps => ({
            ...prevSteps,
            [questionData.id]: selectedOptionData.image  // store the image of the selected option
        }));

        // Log the selected outcomeId
        console.log("Selected Outcome ID:", selectedOutcomeId);

        setTempSelectedOutcomeId(selectedOutcomeId);
        goToNextQuestion();
    };

    const goToNextQuestion = () => {
        setIsExiting(true);

        let updatedTally = { ...outcomeTally };

        if (questionData.id <= 5) {
            // Update the tally here, not in the callback
            updatedTally = {
                ...outcomeTally,
                [tempSelectedOutcomeId]: (outcomeTally[tempSelectedOutcomeId] || 0) + 1
            };
            // Set the updated tally
            setOutcomeTally(updatedTally);
            console.log(`Tallying outcome ID: ${tempSelectedOutcomeId}`, updatedTally);
        } else {
            console.log("Storing selection without tallying for CRM or further use:", tempSelectedOutcomeId);
        }

        const sortedOutcomes = Object.entries(updatedTally).sort((a, b) => b[1] - a[1]);
        const primaryOutcomeId = sortedOutcomes[0][0];

        const timeoutId = setTimeout(() => {
            if (questionData.id === questions.length) {
                setShowStepper(false);  // Hide the stepper after answering the last question
                let url;
                switch (primaryOutcomeId) {
                    case '1': url = 'https://argodesign.com/landing/visionary'; break;
                    case '2': url = 'https://argodesign.com/landing/collaborator'; break;
                    case '3': url = 'https://argodesign.com/landing/transformer'; break;
                    case '4': url = 'https://argodesign.com/landing/transformer'; break;
                    default: url = 'https://argodesign.com'; // Default case if needed
                }
                // Redirect to the determined URL
                window.location.href = url;
            } else {
                const nextQuestionId = questionData.id + 1;
                navigate(`/questions/${nextQuestionId}`);
                console.log(`Navigating to next question after question ID: ${questionData.id}`);
            }
            setIsExiting(false);  // Reset for next render
        }, 677);

        // Cleanup function
        return () => {
            clearTimeout(timeoutId);
        };
    };

    return (
        <Container maxWidth="md" style={{ overflowX: 'hidden' }} >
            <Slide
                direction={isExiting ? 'right' : 'left'}
                in={!isExiting}
                mountOnEnter
                unmountOnExit
                key={questionData.id}
                style={{
                    transitionTimingFunction: theme.transitions.easing.easeInOut,
                }}
                timeout={{
                    enter: 677,  // Adjust this value for the duration when the component enters
                    exit: 677   // Adjust this value for the duration when the component exits
                }}
            >
                <Box display="flex" flexDirection="column" alignItems="left" spacing={3} mx={4} mt={12} mb={16}>
                    <Typography variant="h4" align="left" gutterBottom sx={{ marginBottom: '40px' }}>
                        {questionData.question}
                    </Typography>
                    <RadioGroup value={selectedOption} onChange={handleOptionChange}>
                        <Grid container spacing={3} key={questionData.id}>
                            {shuffledOptions.map((option, index) => (
                                <Grow in={true} timeout="auto">
                                    <Grid item xs={12} sm={6} key={option.id}>
                                        <Card
                                            elevation={hoveredOption === option.id ? 20 : 1}
                                            style={{
                                                border: `1px solid ${selectedOption === option.id ? 'rgb(231, 35, 5)' : 'rgb(245, 245, 245)'}`,
                                                cursor: 'pointer'
                                            }}
                                            onMouseEnter={() => setHoveredOption(option.id)}
                                            onMouseLeave={() => setHoveredOption(null)}
                                            onClick={() => handleOptionChange({ target: { value: option.id } })}
                                        >
                                            <ImageComponent src={option.image} alt={option.text} key={`${questionData.id}-${option.id}`} />
                                            <CardContent>
                                                <FormControlLabel
                                                    value={option.id}
                                                    control={
                                                        <Radio
                                                            sx={{
                                                                '&.MuiRadio-root': {
                                                                    display: 'none'
                                                                }
                                                            }}
                                                        />
                                                    }
                                                    label={<Typography variant="body1">{option.text}</Typography>}
                                                    sx={{ marginLeft: 2, marginRight: 2 }}
                                                />
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grow>
                            ))}
                        </Grid>
                    </RadioGroup>
                </Box>
            </Slide>
            <Slide direction="up" in={showStepper}>
                <Box mt={4} style={{ position: 'fixed', bottom: '0px', left: '0px', right: '0px' }}>
                    <Paper elevation={20} style={{ padding: '16px', margin: '0 auto', maxWidth: '100%', zIndex: 2 }}>
                        <CustomStepper isMobile={isMobile} activeStep={questionData.id - 1} completedSteps={completedSteps} />
                    </Paper>
                </Box>
            </Slide>
        </Container >
    );
};

export default QuestionComponent;
